package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.home.styles.BenefitCardStyle
import com.iregained.site.components.sections.home.styles.BenefitIconStyle
import com.iregained.site.components.sections.home.styles.SecondaryButtonStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.dom.Div

@Composable
private fun Modifier.staggeredZoomIn(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.8f).transition(
            Transition.of("opacity", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms),
            Transition.of("transform", 0.7.s, TransitionTimingFunction.EaseOut, delayMs.ms)
        )
}

@Composable
fun HomeClinicalBenefitsSection(data: HomeClinicalBenefitsSectionData = defaultHomeClinicalBenefitsSectionData) {
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-benefits-section"
    var isVisible by remember { mutableStateOf(false) }
    val palette = ColorMode.current.toSitePalette()

    if (data.benefits.isNotEmpty()) {
        Column(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.custom.lightGray)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(attrs = Modifier.fillMaxWidth().toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element)
                    onDispose { observer.disconnect() }
                }
            }) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).staggeredZoomIn(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 3),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(40.px)
                    ) {
                        data.benefits.take(3).forEachIndexed { index, benefit ->
                            key(benefit.title) {
                                BenefitCard(
                                    iconPath = benefit.iconPath,
                                    title = benefit.title,
                                    description = benefit.description,
                                    modifier = Modifier.staggeredZoomIn(isVisible, 200 + (index * 200))
                                )
                            }
                        }
                    }

                    if (data.ctaLinkPath.isNotBlank() && data.ctaButtonText.isNotBlank()) {
                        Link(
                            path = data.ctaLinkPath,
                            modifier = Modifier.textDecorationLine(TextDecorationLine.None).margin(top = 50.px)
                                .staggeredZoomIn(isVisible, 800)
                        ) {
                            Box(
                                modifier = SecondaryButtonStyle.toModifier()
                                    .padding(leftRight = 32.px, topBottom = 16.px).borderRadius(8.px)
                                    .cursor(Cursor.Pointer), contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    text = data.ctaButtonText,
                                    modifier = Modifier.fontSize(18.px).fontWeight(FontWeight.SemiBold)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BenefitCard(iconPath: String, title: String, description: String, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    Column(
        modifier = BenefitCardStyle.toModifier().then(modifier).fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier.size(100.px).backgroundColor(palette.brand.primarySoft).borderRadius(50.percent)
                .margin(bottom = 20.px), contentAlignment = Alignment.Center
        ) {
            if (iconPath.isNotBlank()) Image(
                src = iconPath,
                modifier = BenefitIconStyle.toModifier().unextractable().size(128.px).borderRadius(50.percent)
                    .objectFit(ObjectFit.Cover)
            )
        }
        if (title.isNotBlank()) SpanText(
            text = title,
            modifier = Modifier.fontSize(20.px).fontWeight(FontWeight.Bold).color(palette.custom.darkText)
                .opacity(0.75f).textAlign(TextAlign.Center).margin(bottom = 12.px)
        )
        Div(
            attrs = Modifier.fontSize(16.px).lineHeight(1.6).color(palette.custom.darkText).textAlign(TextAlign.Center)
            .styleModifier {
                property("display", "-webkit-box"); property(
                "-webkit-line-clamp", "4"
            ); property("-webkit-box-orient", "vertical"); property("overflow", "hidden")
            }.toAttrs { ref { htmlElement -> htmlElement.innerHTML = description; onDispose {} } })
    }
}

data class HomeClinicalBenefitsSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val ctaButtonText: String,
    val ctaLinkPath: String,
    val benefits: List<BenefitData>
)

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field iconPath:
 * - Aspect Ratio: 1:1 (Perfect Square).
 * - Recommended Dimensions: 150x150px.
 * - Format: SVG (Highly Recommended) or transparent PNG.
 * - Max File Size: 50KB.
 * - Behavior: Will be constrained to 100x100px in the UI.
 */
data class BenefitData(val iconPath: String, val title: String, val description: String)

// Default payload...
val defaultHomeClinicalBenefitsSectionData = HomeClinicalBenefitsSectionData(
    sectionId = "home-benefits-section",
    sectionHeader = "DRIVEN BY SCIENCE. DESIGNED FOR LIFE.",
    ctaButtonText = "Explore Clinical Evidence →",
    ctaLinkPath = "resources/#resources-evidence",
    benefits = listOf(
        BenefitData(
            Res.Images.Home.Benefits.NEUROPLASTICITY,
            "Targeted Neuroplasticity",
            "Task-specific, high-repetition protocols formulated to actively rewire neural pathways and restore physical hand function."
        ), BenefitData(
            Res.Images.Home.Benefits.ENGAGEMENT,
            "High Patient Engagement",
            "Proprietary gamified software turns rigorous rehabilitation into an immersive, rewarding experience, drastically increasing adherence."
        ), BenefitData(
            Res.Images.Home.Benefits.ANALYTICS,
            "Clinical Data Analytics",
            "Cloud-integrated dashboard provides therapists with real-time, objective ROM and force metrics to continuously optimize care."
        )
    )
)